// Add an entry after the fact, or edit one: time, project, billable, tags, split, delete, and its version history.
import { useEffect, useState } from 'react';
import type { TimeEntry, TimeEntryVersion } from '../api/types.ts';
import { isoLocalMinute, parseLocal } from '../lib/calendar.ts';
import { activeProjects, project as findProject } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { Confirm, Field, Select, Sheet, TextInput, Toggle, onValue } from '../components/common.tsx';
import { useFmt, useNow, useStore } from '../store/hooks.tsx';
import { Button } from '../ui/ds.tsx';

function parseTags(text: string): string[] {
  const seen = new Set<string>();
  for (const raw of text.split(/[,;]/)) {
    const tag = raw.trim().replace(/^#+/, '').slice(0, 40);
    if (tag) seen.add(tag);
  }
  return [...seen];
}

export function EntryEditor({ entryId, day }: { entryId: string | null; day: number | null }) {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(1000);
  const tz = store.timeZone;
  const existing = entryId ? store.findEntry(entryId) : undefined;
  const initialStart = existing?.startAt ?? (day !== null ? day - (day % 900_000) : now - 3_600_000);
  const [description, setDescription] = useState(existing?.description ?? '');
  const [projectId, setProjectId] = useState<string | null>(existing ? existing.projectId : store.ui.draftProjectId);
  const [start, setStart] = useState(isoLocalMinute(initialStart, tz));
  const [end, setEnd] = useState(isoLocalMinute(existing ? existing.endAt ?? now : day !== null ? initialStart + 3_600_000 : now, tz));
  const [running, setRunning] = useState(existing ? existing.endAt === null : false);
  const [billable, setBillable] = useState(existing?.billable ?? findProject(store.ws, projectId)?.billable ?? true);
  const [tagText, setTagText] = useState((existing?.tags ?? []).join(', '));
  const [versions, setVersions] = useState<TimeEntryVersion[]>([]);
  const [known, setKnown] = useState<string[]>([]);
  const [splitAt, setSplitAt] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => {
    if (entryId) void store.versions(entryId).then(setVersions).catch(() => setVersions([]));
    void store.api.tags().then(setKnown).catch(() => setKnown([]));
    if (existing) setSplitAt(isoLocalMinute(existing.startAt + ((existing.endAt ?? now) - existing.startAt) / 2, tz).slice(11));
  }, [entryId]);
  const close = () => store.closeSheet();
  const startAt = parseLocal(start, tz, existing?.startAt);
  const endAt = running ? null : parseLocal(end, tz, existing?.endAt ?? undefined);
  const valid = startAt !== null && (running || (endAt !== null && endAt > startAt));
  const project = findProject(store.ws, projectId);
  const tags = parseTags(tagText);
  const suggestions = known.filter((k) => !tags.includes(k)).slice(0, 5);
  const projects = [...activeProjects(store.ws), ...store.ws.projects.filter((p) => p.archivedAt !== null && p.id === existing?.projectId)];

  const save = () => {
    if (!valid || startAt === null) return;
    const actor = store.actor();
    const at = store.clock();
    const text = description.trim();
    if (existing) {
      void store.apply(m.saveEntry(actor, existing, { ...existing, description: text, projectId, startAt, endAt, billable, tags }, at));
    } else {
      void store.apply(m.saveEntry(actor, undefined, m.newEntry(actor, at, { description: text, projectId, start: startAt, end: endAt, billable, tags }), at));
    }
    close();
  };
  const split = () => {
    if (!existing || !splitAt) return;
    const date = isoLocalMinute(existing.startAt, tz).slice(0, 10);
    let at = parseLocal(`${date}T${splitAt}`, tz);
    if (at !== null && at <= existing.startAt) at = parseLocal(`${isoLocalMinute((existing.endAt ?? now), tz).slice(0, 10)}T${splitAt}`, tz);
    if (at === null) return;
    void store.apply(m.splitEntry(store.actor(), existing, at, store.clock()));
    close();
  };
  const restore = (version: TimeEntryVersion) => {
    if (!existing) return;
    void store.apply(m.restoreVersion(store.actor(), version, existing, store.otherRunning(existing.id), store.clock()));
    close();
  };
  const summary = (v: TimeEntryVersion) => {
    if (!existing) return '';
    const old: TimeEntry = m.snapshotEntry(v.snapshot, existing);
    return [`${fmt.time(old.startAt)}–${old.endAt === null ? t('jetzt') : fmt.time(old.endAt)}`, findProject(store.ws, old.projectId)?.name ?? t('ohne Projekt'),
      ...(old.description ? [old.description] : []), ...(old.deletedAt !== null ? [t('gelöscht')] : []), ...(!old.billable ? [t('nicht abrechenbar')] : [])].join(' · ');
  };
  const splittable = existing && existing.endAt !== null && existing.endAt - existing.startAt > 120_000;
  return (
    <Sheet title={existing ? t('Eintrag bearbeiten') : t('Eintrag nachtragen')} onClose={close}
      footer={<>
        {existing ? <Button variant="plain" onClick={() => setConfirmDelete(true)}>{t('Löschen …')}</Button> : null}
        <span className="spacer" />
        <Button variant="plain" onClick={close}>{t('Abbrechen')}</Button>
        <Button variant="primary" disabled={!valid} onClick={save}>{t('Sichern')}</Button>
      </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); save(); }}>
        <Field label={t('Beschreibung')}>
          <TextInput autoFocus value={description} placeholder={t('Woran hast du gearbeitet?')} onChange={onValue(setDescription)} />
        </Field>
        <Field label={t('Projekt')}>
          <Select value={projectId ?? ''} onChange={(e) => {
            const id = e.target.value || null;
            setProjectId(id);
            setBillable(findProject(store.ws, id)?.billable ?? true);
          }}>
            <option value="">{t('Ohne Projekt')}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <div className="form-row">
          <Field label={t('Von')}><TextInput type="datetime-local" value={start} onChange={onValue(setStart)} /></Field>
          {running ? (
            <Field label={t('Bis')}><span className="settings-value">{t('läuft')}</span></Field>
          ) : (
            <Field label={t('Bis')}><TextInput type="datetime-local" value={end} onChange={onValue(setEnd)} /></Field>
          )}
        </div>
        {existing && existing.endAt === null ? (
          <Field inline label={t('Läuft noch')}><Toggle checked={running} onChange={(v) => { setRunning(v); if (!v) setEnd(isoLocalMinute(now, tz)); }} label={t('Läuft noch')} /></Field>
        ) : null}
        <Field inline label={t('Abrechenbar')} hint={project && !project.billable ? t('Das Projekt ist intern.') : undefined}>
          <Toggle checked={billable && (project?.billable ?? true)} disabled={project ? !project.billable : false} onChange={setBillable} label={t('Abrechenbar')} />
        </Field>
        <Field label={t('Tags')}>
          <TextInput value={tagText} placeholder={t('z. B. review, kunde')} onChange={onValue(setTagText)} />
        </Field>
        {suggestions.length > 0 ? (
          <div className="chips">
            <span className="note note--faint">{t('Vorschläge:')}</span>
            {suggestions.map((tag) => <Button key={tag} size="sm" onClick={() => setTagText([...tags, tag].join(', '))}>{`#${tag}`}</Button>)}
          </div>
        ) : null}
        <Field label={t('Dauer')}>
          <span className="settings-value mono-num">{valid && startAt !== null ? fmt.clock((endAt ?? now) - startAt) : '–'}</span>
        </Field>
        {!valid ? <p className="field__error">{t('Das Ende muss nach dem Beginn liegen.')}</p> : null}
        <button type="submit" hidden />
      </form>
      {existing ? (
        <div className="editor-section">
          <div className="tl-label">{t('Teilen')}</div>
          {splittable ? (
            <div className="split-row">
              <TextInput type="time" value={splitAt} aria-label={t('Teilen um')} onChange={onValue(setSplitAt)} className="input--time" />
              <Button size="sm" onClick={split}>{t('In zwei Einträge teilen')}</Button>
              <span className="note note--faint">{t('Beide Teile behalten Beschreibung, Projekt und Tags.')}</span>
            </div>
          ) : <p className="note note--faint">{existing.endAt === null ? t('Stoppe den Timer, um den Eintrag zu teilen.') : t('Zu kurz zum Teilen.')}</p>}
        </div>
      ) : null}
      {versions.length > 0 ? (
        <div className="editor-section">
          <div className="tl-label">{t('Verlauf')}</div>
          <div className="versions">
            {versions.map((v) => (
              <div key={v.id} className="version">
                <span className="version__text">
                  <span className="version__title">{t('Version %lld · geändert %@', v.version, fmt.started(v.editedAt, now))}</span>
                  <span className="note">{summary(v)}</span>
                </span>
                <Button variant="plain" size="sm" onClick={() => restore(v)}>{t('Wiederherstellen')}</Button>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {confirmDelete && existing ? (
        <Confirm title={t('Eintrag löschen?')} danger confirm={t('Löschen')} cancel={t('Abbrechen')} onClose={() => setConfirmDelete(false)}
          message={t('Der Eintrag bleibt im Verlauf und lässt sich wiederherstellen.')}
          onConfirm={() => { void store.apply(m.deleteEntries(store.actor(), [existing], store.clock())); close(); }} />
      ) : null}
    </Sheet>
  );
}
